import { buildProfilesOptionsFetcher, ProfileOptionOriginal } from "PFApp/helpers/options/profiles_options";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Modal } from "PFComponents/modal";
import { Pill } from "PFComponents/pill";
import { Select } from "PFComponents/select";
import api from "PFCore/api";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Conversation, Option } from "PFTypes";
import { ChatParticipant } from "PFTypes/chat_participant";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type InviteModalProps = {
  conversation: Conversation;
  participants: ChatParticipant[];
  handleClose: () => void;
};

export const InviteModal = ({ conversation, participants, handleClose }: InviteModalProps) => {
  const [items, setItems] = useState<Option<ProfileOptionOriginal>[]>([]);
  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation();
  const { invalidateParticipants } = useChatInvalidate();

  const fetchOptions = useMemo(
    () =>
      buildProfilesOptionsFetcher({
        excludeProfileId: currentProfile.id,
        getDisabled: (profile) =>
          !!participants.find(({ profile: participant }) => participant.id === profile.id),
        renderDisplayElement: (profile) => {
          const alreadyParticipates = !!participants.find(
            ({ profile: participant }) => participant.id === profile.id
          );

          return (
            <>
              {alreadyParticipates && <Pill text={t("conversations.participant")} small />}
              <span>{` ${profile.text}`}</span>
            </>
          );
        }
      }),
    [currentProfile.id, participants, t]
  );

  return (
    <Modal
      title={t("conversations.inviteToConversation", { title: conversation?.title || "" })}
      onClose={handleClose}
      labelOK={t("conversations.inviteParticipants", { count: items.length })}
      showOKButton={items.length > 0}
      onOK={() => {
        Promise.all(
          items.map(({ id }) =>
            api({
              url: "chat/participants",
              method: "POST",
              data: { conversationId: conversation.id, profileId: id }
            })
          )
        ).then(() => {
          invalidateParticipants(conversation.id);
          showGrowl({
            message: t("conversations.invitedToConvo", {
              count: items.length,
              invited: items.length === 1 ? items[0].value : items.length
            })
          });
        });
      }}
    >
      <Select
        placeholder={t("conversations.userNamePlaceholder")}
        cacheTime={0}
        multiple={true}
        value={items.map((item) => item.id)}
        onChange={(options) => setItems(Array.isArray(options) ? options : [options])}
        fetchOptions={fetchOptions}
      />
      {items.length > 0 && (
        <p style={{ marginTop: 30 }}>
          {t("conversations.participantWillReceiveEmail", { count: items.length })}
        </p>
      )}
    </Modal>
  );
};
